import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { handleStructuredPaste } from '@/lib/pasteUtils';
import { toast } from 'sonner';
import { 
  Plus, 
  Receipt,
  Car,
  Utensils,
  Hotel,
  Plane,
  CreditCard,
  Package,
  Users,
  MoreHorizontal,
  Trash2,
  Download,
  Filter,
  Loader2,
  DollarSign,
  FileText,
  FileSpreadsheet,
  Upload,
  Image,
  X,
  Pencil,
  ExternalLink,
  Maximize2,
  CheckCircle,
  Circle
} from 'lucide-react';
import ImageLightbox from '@/components/ImageLightbox';

const CATEGORY_ICONS = {
  mileage: Car,
  receipts: Receipt,
  meals: Utensils,
  travel_hotel: Hotel,
  travel_air: Plane,
  subscriptions: CreditCard,
  materials: Package,
  contractor: Users,
  other: MoreHorizontal
};

const CATEGORY_LABELS = {
  mileage: 'Mileage',
  receipts: 'Receipts',
  meals: 'Meals',
  travel_hotel: 'Travel - Hotel',
  travel_air: 'Travel - Air',
  subscriptions: 'Subscriptions',
  materials: 'Materials',
  contractor: 'Contractor',
  other: 'Other'
};

const ExpensesPage = () => {
  const { token, isFinance } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedJobId = searchParams.get('job_id');
  
  const [expenses, setExpenses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mileageRate, setMileageRate] = useState(0.67);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterJobId, setFilterJobId] = useState(preselectedJobId || 'all');
  const [filterPaidStatus, setFilterPaidStatus] = useState('all');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const [editingExpense, setEditingExpense] = useState(null);
  
  const initialFormState = {
    job_id: preselectedJobId || '',
    category: '',
    customCategory: '',
    amount: '',
    miles: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    receipt_url: ''
  };
  
  const [newExpense, setNewExpense] = useState(initialFormState);

  const fetchData = async () => {
    try {
      const [expensesRes, jobsRes, categoriesRes, rateRes] = await Promise.all([
        axios.get(`${API}/expenses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expense-categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/mileage-rate`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setExpenses(expensesRes.data);
      setJobs(jobsRes.data);
      setCategories(categoriesRes.data.categories);
      setMileageRate(rateRes.data.rate);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const uploadReceiptToCloudinary = async (file) => {
    // Receipts now go to Google Drive. Function name retained to minimize churn.
    try {
      setUploadingReceipt(true);
      const expenseDate = (newExpense && newExpense.date) || (editingExpense && editingExpense.date) || new Date().toISOString().slice(0,10);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/receipts/upload?expense_date=${encodeURIComponent(expenseDate)}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return res.data.receipt_url;
    } catch (err) {
      console.error('Receipt upload error:', err);
      const detail = err?.response?.data?.detail || err.message || 'Upload failed';
      // Surface the real reason so the user knows what to do
      toast.error(`Receipt upload failed: ${detail}`);
      throw err;
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleReceiptSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setReceiptFile(file);
  };

  const handleRemoveReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    setNewExpense({ ...newExpense, receipt_url: '' });
  };

  const handleAddExpense = async () => {
    if (!newExpense.category) {
      toast.error('Please select a category');
      return;
    }
    if (newExpense.category === 'other' && !newExpense.customCategory) {
      toast.error('Please specify the expense type');
      return;
    }
    if (newExpense.category === 'mileage' && !newExpense.miles) {
      toast.error('Please enter miles');
      return;
    }
    if (newExpense.category !== 'mileage' && !newExpense.amount) {
      toast.error('Please enter an amount');
      return;
    }

    setSaving(true);
    try {
      // Upload receipt if selected
      let receiptUrl = newExpense.receipt_url;
      if (receiptFile) {
        receiptUrl = await uploadReceiptToCloudinary(receiptFile);
      }

      // Build description including custom category if "other"
      let finalDescription = newExpense.description;
      if (newExpense.category === 'other' && newExpense.customCategory) {
        finalDescription = `[${newExpense.customCategory}] ${newExpense.description || ''}`.trim();
      }
      
      const payload = {
        job_id: newExpense.job_id || null,
        category: newExpense.category,
        description: finalDescription,
        amount: newExpense.category === 'mileage' 
          ? parseFloat(newExpense.miles) * mileageRate 
          : parseFloat(newExpense.amount),
        miles: newExpense.category === 'mileage' ? parseFloat(newExpense.miles) : null,
        date: newExpense.date,
        receipt_url: receiptUrl
      };
      
      await axios.post(`${API}/expenses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense added');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setNewExpense(initialFormState);
    setReceiptFile(null);
    setReceiptPreview(null);
  };

  const handleOpenEdit = (expense) => {
    setEditingExpense({
      ...expense,
      amount: expense.amount?.toString() || '',
      miles: expense.miles?.toString() || '',
      customCategory: expense.category === 'other' ? expense.description?.match(/\[(.*?)\]/)?.[1] || '' : ''
    });
    setReceiptPreview(expense.receipt_url || null);
    setEditDialogOpen(true);
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;
    
    // Validation
    if (editingExpense.category === 'mileage' && !editingExpense.miles) {
      toast.error('Please enter miles');
      return;
    }
    if (editingExpense.category !== 'mileage' && !editingExpense.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    setSaving(true);
    try {
      // Upload new receipt if selected
      let receiptUrl = editingExpense.receipt_url;
      if (receiptFile) {
        receiptUrl = await uploadReceiptToCloudinary(receiptFile);
      }

      // Build description including custom category if "other"
      let finalDescription = editingExpense.description || '';
      if (editingExpense.category === 'other' && editingExpense.customCategory) {
        // Remove old custom category tag if present
        finalDescription = finalDescription.replace(/\[.*?\]\s*/, '');
        finalDescription = `[${editingExpense.customCategory}] ${finalDescription}`.trim();
      }

      // Full expense update
      await axios.put(`${API}/expenses/${editingExpense.id}`, {
        category: editingExpense.category,
        amount: editingExpense.category === 'mileage' 
          ? parseFloat(editingExpense.miles) * mileageRate 
          : parseFloat(editingExpense.amount),
        miles: editingExpense.category === 'mileage' ? parseFloat(editingExpense.miles) : null,
        description: finalDescription,
        date: editingExpense.date,
        receipt_url: receiptUrl,
        job_id: editingExpense.job_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Expense updated');
      setEditDialogOpen(false);
      setEditingExpense(null);
      setReceiptFile(null);
      setReceiptPreview(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete expense');
    }
  };

  const handleMarkPaid = async (expenseId, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await axios.patch(`${API}/expenses/${expenseId}/paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update expense');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/expenses/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/expenses/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel file downloaded');
    } catch (err) {
      toast.error('Failed to export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/export/expenses/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to export PDF');
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    const matchesJob = filterJobId === 'all' || expense.job_id === filterJobId;
    const matchesPaidStatus = filterPaidStatus === 'all' || 
      (filterPaidStatus === 'paid' && expense.paid) || 
      (filterPaidStatus === 'unpaid' && !expense.paid);
    return matchesCategory && matchesJob && matchesPaidStatus;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const paidExpenses = expenses.filter(e => e.paid).reduce((sum, e) => sum + e.amount, 0);
  const unpaidExpenses = expenses.filter(e => !e.paid).reduce((sum, e) => sum + e.amount, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const categoryTotals = filteredExpenses.reduce((acc, expense) => {
    acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  const renderReceiptUpload = (currentUrl, isEdit = false) => (
    <div className="space-y-2">
      <Label>Receipt Image</Label>
      {(receiptPreview || currentUrl) ? (
        <div className="relative group">
          <img 
            src={receiptPreview || currentUrl} 
            alt="Receipt" 
            className="w-full h-40 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => {
              setLightboxImage(receiptPreview || currentUrl);
              setLightboxOpen(true);
            }}
            data-testid="receipt-image-preview"
          />
          {/* Click to view overlay */}
          <div 
            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg cursor-pointer"
            onClick={() => {
              setLightboxImage(receiptPreview || currentUrl);
              setLightboxOpen(true);
            }}
          >
            <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <Maximize2 className="w-4 h-4" />
              Click to view full size
            </span>
          </div>
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImage(receiptPreview || currentUrl);
                setLightboxOpen(true);
              }}
              className="h-8 w-8 bg-white/90"
              title="View full size"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={handleRemoveReceipt}
              className="h-8 w-8"
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#1F3C4F] transition-colors">
          <Upload className="w-8 h-8 text-slate-400" />
          <span className="mt-2 text-sm text-slate-500">Click to upload receipt</span>
          <span className="text-xs text-slate-400">JPG, PNG up to 10MB</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleReceiptSelect}
          />
        </label>
      )}
      {uploadingReceipt && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Uploading...
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Expenses</h1>
          <p className="text-slate-500 mt-1">Track business expenses and receipts</p>
        </div>
        <div className="flex gap-3">
          {isFinance && (
            <>
              <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Expense</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={newExpense.category}
                    onValueChange={(value) => setNewExpense({ ...newExpense, category: value, amount: '', miles: '', customCategory: '' })}
                  >
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                        return (
                          <SelectItem key={cat} value={cat}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {CATEGORY_LABELS[cat]}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {newExpense.category === 'other' && (
                  <div className="space-y-2">
                    <Label htmlFor="customCategory">Specify Expense Type *</Label>
                    <Input
                      id="customCategory"
                      placeholder="e.g., Office Supplies, Equipment Rental..."
                      value={newExpense.customCategory}
                      onChange={(e) => setNewExpense({ ...newExpense, customCategory: e.target.value })}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Job (Optional)</Label>
                  <Select 
                    value={newExpense.job_id || "none"}
                    onValueChange={(value) => setNewExpense({ ...newExpense, job_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Not linked to a job" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked to a job</SelectItem>
                      {jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.job_number} - {job.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {newExpense.category === 'mileage' ? (
                  <div className="space-y-2">
                    <Label htmlFor="miles">Miles *</Label>
                    <Input
                      id="miles"
                      type="number"
                      step="0.1"
                      placeholder="Enter miles"
                      value={newExpense.miles}
                      onChange={(e) => setNewExpense({ ...newExpense, miles: e.target.value })}
                      data-testid="expense-miles-input"
                    />
                    <p className="text-sm text-slate-500">
                      Rate: ${mileageRate}/mile • Amount: {formatCurrency(parseFloat(newExpense.miles || 0) * mileageRate)}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="pl-9"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                        data-testid="expense-amount-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="expense-date">Date</Label>
                  <Input
                    id="expense-date"
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Expense details..."
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    onPaste={handleStructuredPaste}
                    rows={2}
                  />
                </div>

                {renderReceiptUpload(newExpense.receipt_url)}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button 
                  onClick={handleAddExpense} 
                  disabled={saving || uploadingReceipt}
                  className="bg-[#1F3C4F] hover:bg-[#152a38]"
                  data-testid="expense-submit-btn"
                >
                  {(saving || uploadingReceipt) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Add Expense
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog - Full Editing */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingExpense(null); setReceiptFile(null); setReceiptPreview(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Expense</DialogTitle>
          </DialogHeader>
          {editingExpense && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={editingExpense.category}
                  onValueChange={(value) => setEditingExpense({ 
                    ...editingExpense, 
                    category: value, 
                    amount: value === 'mileage' ? '' : editingExpense.amount,
                    miles: value !== 'mileage' ? '' : editingExpense.miles,
                    customCategory: value === 'other' ? editingExpense.customCategory : ''
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                      return (
                        <SelectItem key={cat} value={cat}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {CATEGORY_LABELS[cat]}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {editingExpense.category === 'other' && (
                <div className="space-y-2">
                  <Label>Specify Expense Type</Label>
                  <Input
                    placeholder="e.g., Office Supplies, Equipment Rental..."
                    value={editingExpense.customCategory || ''}
                    onChange={(e) => setEditingExpense({ ...editingExpense, customCategory: e.target.value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Job (Optional)</Label>
                <Select 
                  value={editingExpense.job_id || "none"}
                  onValueChange={(value) => setEditingExpense({ ...editingExpense, job_id: value === "none" ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not linked to a job" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked to a job</SelectItem>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.job_number} - {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editingExpense.category === 'mileage' ? (
                <div className="space-y-2">
                  <Label>Miles</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Enter miles"
                    value={editingExpense.miles || ''}
                    onChange={(e) => setEditingExpense({ ...editingExpense, miles: e.target.value })}
                  />
                  <p className="text-sm text-slate-500">
                    Rate: ${mileageRate}/mile • Amount: {formatCurrency(parseFloat(editingExpense.miles || 0) * mileageRate)}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-9"
                      value={editingExpense.amount || ''}
                      onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editingExpense.date || ''}
                  onChange={(e) => setEditingExpense({ ...editingExpense, date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Expense details..."
                  value={editingExpense.description?.replace(/\[.*?\]\s*/, '') || ''}
                  onChange={(e) => setEditingExpense({ ...editingExpense, description: e.target.value })}
                  onPaste={handleStructuredPaste}
                  rows={2}
                />
              </div>

              {renderReceiptUpload(editingExpense.receipt_url, true)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateExpense}
              disabled={saving || uploadingReceipt}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
            >
              {(saving || uploadingReceipt) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-[#FF1493] to-[#CC0F75]">
          <CardContent className="p-5">
            <div className="text-white">
              <p className="text-white/70 text-sm">Total Expenses</p>
              <p className="text-2xl font-heading font-bold mt-1">{formatCurrency(totalExpenses)}</p>
              <p className="text-white/70 text-xs mt-1">{filteredExpenses.length} entries</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterPaidStatus === 'unpaid' ? 'ring-2 ring-[#FF1493]' : ''}`}
          onClick={() => setFilterPaidStatus(filterPaidStatus === 'unpaid' ? 'all' : 'unpaid')}
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Circle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Unpaid</p>
                <p className="text-lg font-heading font-bold text-amber-600">{formatCurrency(unpaidExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-0 shadow-sm cursor-pointer transition-all ${filterPaidStatus === 'paid' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilterPaidStatus(filterPaidStatus === 'paid' ? 'all' : 'paid')}
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Paid</p>
                <p className="text-lg font-heading font-bold text-green-600">{formatCurrency(paidExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <Label className="text-xs text-slate-500">Filter by Category</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="mt-1 h-8">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <Label className="text-xs text-slate-500">Filter by Job</Label>
            <Select value={filterJobId} onValueChange={setFilterJobId}>
              <SelectTrigger className="mt-1 h-8">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map((job) => (
                  <SelectItem key={job.id} value={job.id}>{job.job_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Category Summary - Clickable */}
      {Object.keys(categoryTotals).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">By Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Object.entries(categoryTotals).map(([category, total]) => {
                const Icon = CATEGORY_ICONS[category] || MoreHorizontal;
                return (
                  <div 
                    key={category} 
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => setFilterCategory(category)}
                  >
                    <div className="p-2 bg-white rounded-lg">
                      <Icon className="w-4 h-4 text-[#1F3C4F]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{CATEGORY_LABELS[category]}</p>
                      <p className="font-heading font-semibold">{formatCurrency(total)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses List - Clickable Cards */}
      {filteredExpenses.length > 0 ? (
        <div className="space-y-3" data-testid="expenses-list">
          {filteredExpenses.map((expense) => {
            const Icon = CATEGORY_ICONS[expense.category] || MoreHorizontal;
            const isPaid = expense.paid;
            return (
              <Card 
                key={expense.id} 
                className={`border-0 shadow-sm hover:shadow-md transition-all cursor-pointer ${isPaid ? 'bg-green-50/50' : ''}`}
                onClick={() => handleOpenEdit(expense)}
                data-testid={`expense-${expense.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPaid ? 'bg-green-100' : 'bg-[#FF1493]/10'}`}>
                        <Icon className={`w-5 h-5 ${isPaid ? 'text-green-600' : 'text-[#FF1493]'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium capitalize">{CATEGORY_LABELS[expense.category]}</span>
                          {expense.job_number && (
                            <span className="font-mono text-sm text-[#1F3C4F]">{expense.job_number}</span>
                          )}
                          {expense.receipt_url && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              <Image className="w-3 h-3" /> Receipt
                            </span>
                          )}
                          {isPaid && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded font-medium">
                              <CheckCircle className="w-3 h-3" /> Paid
                            </span>
                          )}
                          {expense.billed && !isPaid && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              Billed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {expense.description || 'No description'} • {new Date(expense.date).toLocaleDateString()}
                        </p>
                        {expense.miles && (
                          <p className="text-xs text-slate-400">{expense.miles} miles</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className={`text-xl font-heading font-bold ${isPaid ? 'text-green-600' : 'text-[#FF1493]'}`}>{formatCurrency(expense.amount)}</p>
                        <p className="text-xs text-slate-500">{expense.user_name}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleMarkPaid(expense.id, e)}
                        className={isPaid ? 'text-green-600 hover:text-green-800 hover:bg-green-100' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}
                        title={isPaid ? 'Mark as unpaid' : 'Mark as paid'}
                      >
                        {isPaid ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleOpenEdit(expense); }}
                        className="text-slate-400 hover:text-[#1F3C4F]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Receipt className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700">No expenses found</h3>
            <p className="text-slate-500 mt-1">Add your first expense to start tracking</p>
          </CardContent>
        </Card>
      )}

      {/* Image Lightbox for viewing receipts */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={setLightboxOpen}
        imageUrl={lightboxImage}
        altText="Receipt"
        title="Receipt Image"
      />
    </div>
  );
};

export default ExpensesPage;
